import React from 'react';

// Botanical · Celestial archetype illustrations
// Each SVG uses currentColor for strokes (pass via CSS color) and
// amethyst/sage/moss accent fills for atmospheric tint.
// viewBox is 0 0 100 100 for easy sizing.

const BASE = {
  viewBox: '0 0 100 100',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.4,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

const AMETHYST = '#3D3830';
const SAGE = '#B8A78F';
const MOSS = '#3D3830';

export const VisionaryIllustration = ({ size = 120, ...rest }) => (
  <svg {...BASE} width={size} height={size} {...rest} role="img" aria-label="Visionary. iris bloom and shooting star">
    {/* Shooting star trail */}
    <path d="M22 14 L40 30" opacity="0.5" />
    {/* 4-point star */}
    <path
      d="M46 24 L50 32 L58 36 L50 40 L46 48 L42 40 L34 36 L42 32 Z"
      fill={AMETHYST}
      fillOpacity="0.16"
    />
    <circle cx="72" cy="20" r="0.9" fill="currentColor" />
    <circle cx="80" cy="30" r="0.7" fill="currentColor" />
    <circle cx="26" cy="44" r="0.6" fill="currentColor" />
    {/* Iris. stem */}
    <path d="M50 56 L50 94" />
    {/* Iris. centre standard */}
    <path d="M50 56 Q46 50 46 44 Q50 40 54 44 Q54 50 50 56" fill={AMETHYST} fillOpacity="0.10" />
    {/* Left standard */}
    <path d="M46 58 Q38 52 38 44 Q44 42 48 48 Q50 52 50 58" fill={AMETHYST} fillOpacity="0.08" />
    {/* Right standard */}
    <path d="M54 58 Q62 52 62 44 Q56 42 52 48 Q50 52 50 58" fill={AMETHYST} fillOpacity="0.08" />
    {/* Left fall */}
    <path d="M44 64 Q34 72 36 86 Q46 82 50 68" fill={AMETHYST} fillOpacity="0.06" />
    {/* Right fall */}
    <path d="M56 64 Q66 72 64 86 Q54 82 50 68" fill={AMETHYST} fillOpacity="0.06" />
    {/* Sword leaves */}
    <path d="M50 82 Q44 88 40 96" />
    <path d="M50 86 Q56 92 60 98" opacity="0.65" />
  </svg>
);

export const WarriorIllustration = ({ size = 120, ...rest }) => (
  <svg {...BASE} width={size} height={size} {...rest} role="img" aria-label="Warrior. waxing crescent and thistle">
    {/* Waxing crescent moon: full circle + dark crescent cut on left */}
    <circle cx="50" cy="22" r="10" fill={AMETHYST} fillOpacity="0.10" />
    <path d="M50 12 A10 10 0 0 0 50 32 A6 10 0 0 1 50 12 Z" fill="currentColor" fillOpacity="0.25" />
    {/* Stars */}
    <circle cx="28" cy="18" r="0.8" fill="currentColor" />
    <circle cx="74" cy="14" r="0.7" fill="currentColor" />
    {/* Thistle head (spiky ellipse) */}
    <ellipse cx="50" cy="56" rx="7" ry="9" fill={MOSS} fillOpacity="0.10" />
    {/* Bristles from thistle crown */}
    <path d="M50 47 L50 41" />
    <path d="M46 48 L43 43" />
    <path d="M54 48 L57 43" />
    <path d="M43 51 L38 48" />
    <path d="M57 51 L62 48" />
    {/* Thistle base cross-hatch */}
    <path d="M44 60 L56 60" />
    <path d="M45 63 L55 63" opacity="0.6" />
    {/* Stem */}
    <path d="M50 65 L50 94" />
    {/* Prickly leaves */}
    <path d="M50 74 L40 70" />
    <path d="M40 70 L44 76" />
    <path d="M40 70 L36 72" />
    <path d="M50 82 L60 78" />
    <path d="M60 78 L58 84" />
    <path d="M60 78 L64 80" />
  </svg>
);

export const ScholarIllustration = ({ size = 120, ...rest }) => (
  <svg {...BASE} width={size} height={size} {...rest} role="img" aria-label="Scholar. waning gibbous moon and sage sprig">
    {/* Waning gibbous: moon with small bite on right */}
    <circle cx="50" cy="22" r="10" fill={AMETHYST} fillOpacity="0.10" />
    <path d="M56 12 A10 10 0 0 1 56 32 A5 10 0 0 0 56 12 Z" fill="currentColor" fillOpacity="0.18" />
    {/* Dots */}
    <circle cx="28" cy="26" r="0.6" fill="currentColor" />
    <circle cx="74" cy="20" r="0.7" fill="currentColor" />
    {/* Sage sprig. stem */}
    <path d="M50 40 L50 94" />
    {/* Paired oval leaves */}
    <g fill={SAGE} fillOpacity="0.18">
      <ellipse cx="42" cy="50" rx="6.5" ry="2.8" transform="rotate(-30 42 50)" />
      <ellipse cx="58" cy="50" rx="6.5" ry="2.8" transform="rotate(30 58 50)" />
      <ellipse cx="40" cy="64" rx="7.5" ry="3.2" transform="rotate(-28 40 64)" />
      <ellipse cx="60" cy="64" rx="7.5" ry="3.2" transform="rotate(28 60 64)" />
      <ellipse cx="42" cy="78" rx="6.5" ry="2.8" transform="rotate(-30 42 78)" />
      <ellipse cx="58" cy="78" rx="6.5" ry="2.8" transform="rotate(30 58 78)" />
    </g>
  </svg>
);

export const FeelerIllustration = ({ size = 120, ...rest }) => (
  <svg {...BASE} width={size} height={size} {...rest} role="img" aria-label="Feeler. full moon and unfurling rose">
    {/* Full moon */}
    <circle cx="50" cy="18" r="10" fill={AMETHYST} fillOpacity="0.14" />
    <circle cx="46" cy="16" r="1.2" fill="currentColor" fillOpacity="0.35" />
    <circle cx="53" cy="20" r="0.9" fill="currentColor" fillOpacity="0.3" />
    <circle cx="49" cy="22" r="0.6" fill="currentColor" fillOpacity="0.25" />
    {/* Soft halo */}
    <circle cx="50" cy="18" r="13" opacity="0.3" strokeDasharray="1 2" />
    {/* Distant star */}
    <circle cx="24" cy="26" r="0.6" fill="currentColor" />
    <circle cx="78" cy="24" r="0.6" fill="currentColor" />
    {/* Rose. layered petals (fuller, more romantic) */}
    {/* Innermost bud */}
    <path d="M50 56 Q52 57 52 58.5 Q52 60 50 60.5 Q48 60 48 58.5 Q48 57 50 56" fill={AMETHYST} fillOpacity="0.35" />
    {/* Second ring */}
    <path d="M50 53 Q55 55 55 58.5 Q55 62 50 63 Q45 62 45 58.5 Q45 55 50 53" fill={AMETHYST} fillOpacity="0.18" />
    {/* Third ring */}
    <path d="M50 50 Q58 53 58 58.5 Q58 64 50 65.5 Q42 64 42 58.5 Q42 53 50 50" fill={AMETHYST} fillOpacity="0.12" />
    {/* Outer bloom */}
    <path d="M50 46 Q62 50 62 60 Q59 68 50 68 Q41 68 38 60 Q38 50 50 46" />
    {/* Outer side curves */}
    <path d="M38 60 Q32 54 36 46" opacity="0.65" />
    <path d="M62 60 Q68 54 64 46" opacity="0.65" />
    {/* Outer-most soft petal edges */}
    <path d="M44 48 Q40 44 40 40" opacity="0.4" />
    <path d="M56 48 Q60 44 60 40" opacity="0.4" />
    {/* Stem */}
    <path d="M50 68 L50 94" />
    {/* Leaves */}
    <path d="M50 74 Q42 74 40 68" />
    <path d="M42 68 Q40 70 42 73" opacity="0.5" />
    <path d="M50 82 Q58 82 60 76" />
    <path d="M58 76 Q60 78 58 81" opacity="0.5" />
    <path d="M50 88 Q44 88 42 84" opacity="0.7" />
  </svg>
);

export const OracleIllustration = ({ size = 120, ...rest }) => (
  <svg {...BASE} width={size} height={size} {...rest} role="img" aria-label="Oracle. crescent cradling a star and mugwort">
    {/* Crescent moon open right */}
    <path
      d="M46 10 A12 12 0 1 0 46 34 A8 10 0 1 1 46 10 Z"
      fill={AMETHYST}
      fillOpacity="0.14"
    />
    {/* Star in the crescent cradle */}
    <path
      d="M56 21 L57.6 24 L61 25 L57.6 26 L56 29 L54.4 26 L51 25 L54.4 24 Z"
      fill="currentColor"
      fillOpacity="0.35"
    />
    {/* Distant stars */}
    <circle cx="22" cy="20" r="0.7" fill="currentColor" />
    <circle cx="78" cy="30" r="0.6" fill="currentColor" />
    <circle cx="30" cy="38" r="0.5" fill="currentColor" />
    <circle cx="72" cy="42" r="0.5" fill="currentColor" />
    {/* Mugwort. central stem */}
    <path d="M50 42 L50 94" />
    {/* Lacy feathery fronds. many fine lines, alternating */}
    <g opacity="0.78">
      {/* Pair 1 (top) */}
      <path d="M50 50 L38 44" />
      <path d="M50 50 L40 46" />
      <path d="M50 50 L42 48" />
      <path d="M50 50 L62 44" />
      <path d="M50 50 L60 46" />
      <path d="M50 50 L58 48" />
      {/* Pair 2 */}
      <path d="M50 58 L36 52" />
      <path d="M50 58 L38 54" />
      <path d="M50 58 L40 56" />
      <path d="M50 58 L64 52" />
      <path d="M50 58 L62 54" />
      <path d="M50 58 L60 56" />
      {/* Pair 3 */}
      <path d="M50 66 L34 60" />
      <path d="M50 66 L36 62" />
      <path d="M50 66 L38 64" />
      <path d="M50 66 L66 60" />
      <path d="M50 66 L64 62" />
      <path d="M50 66 L62 64" />
      {/* Pair 4 */}
      <path d="M50 74 L36 68" />
      <path d="M50 74 L38 70" />
      <path d="M50 74 L40 72" />
      <path d="M50 74 L64 68" />
      <path d="M50 74 L62 70" />
      <path d="M50 74 L60 72" />
      {/* Pair 5 */}
      <path d="M50 82 L38 76" />
      <path d="M50 82 L40 78" />
      <path d="M50 82 L42 80" />
      <path d="M50 82 L62 76" />
      <path d="M50 82 L60 78" />
      <path d="M50 82 L58 80" />
      {/* Pair 6 (tip) */}
      <path d="M50 90 L42 86" />
      <path d="M50 90 L44 88" />
      <path d="M50 90 L58 86" />
      <path d="M50 90 L56 88" />
    </g>
  </svg>
);

export const NurturerIllustration = ({ size = 120, ...rest }) => (
  <svg {...BASE} width={size} height={size} {...rest} role="img" aria-label="Nurturer. sun and chamomile flower">
    {/* Sun core */}
    <circle cx="50" cy="20" r="6" fill={MOSS} fillOpacity="0.22" />
    {/* Sun rays - 12 around */}
    <g strokeLinecap="round">
      <path d="M50 8 L50 12" />
      <path d="M50 28 L50 32" />
      <path d="M34 20 L38 20" />
      <path d="M62 20 L66 20" />
      <path d="M38 8 L41 11" />
      <path d="M59 11 L62 8" />
      <path d="M38 32 L41 29" />
      <path d="M59 29 L62 32" />
      <path d="M44 9 L45 12" opacity="0.5" />
      <path d="M55 12 L56 9" opacity="0.5" />
      <path d="M44 31 L45 28" opacity="0.5" />
      <path d="M55 28 L56 31" opacity="0.5" />
    </g>
    {/* Distant stars */}
    <circle cx="24" cy="36" r="0.6" fill="currentColor" />
    <circle cx="76" cy="36" r="0.5" fill="currentColor" />
    {/* Chamomile centre (fuller) */}
    <circle cx="50" cy="58" r="3.5" fill={MOSS} fillOpacity="0.38" />
    <circle cx="50" cy="58" r="1.2" fill="currentColor" fillOpacity="0.3" />
    {/* 12 petals (fuller flower) */}
    <g fill={AMETHYST} fillOpacity="0.12">
      <ellipse cx="50" cy="48" rx="2.6" ry="5" />
      <ellipse cx="50" cy="68" rx="2.6" ry="5" />
      <ellipse cx="40" cy="58" rx="5" ry="2.6" />
      <ellipse cx="60" cy="58" rx="5" ry="2.6" />
      <ellipse cx="43" cy="51" rx="3.6" ry="2.6" transform="rotate(-45 43 51)" />
      <ellipse cx="57" cy="51" rx="3.6" ry="2.6" transform="rotate(45 57 51)" />
      <ellipse cx="43" cy="65" rx="3.6" ry="2.6" transform="rotate(45 43 65)" />
      <ellipse cx="57" cy="65" rx="3.6" ry="2.6" transform="rotate(-45 57 65)" />
      <ellipse cx="46" cy="49" rx="2" ry="4.4" transform="rotate(-22 46 49)" />
      <ellipse cx="54" cy="49" rx="2" ry="4.4" transform="rotate(22 54 49)" />
      <ellipse cx="46" cy="67" rx="2" ry="4.4" transform="rotate(22 46 67)" />
      <ellipse cx="54" cy="67" rx="2" ry="4.4" transform="rotate(-22 54 67)" />
    </g>
    {/* Stem */}
    <path d="M50 74 L50 94" />
    {/* Leaves (branching) */}
    <path d="M50 80 Q42 80 40 74" />
    <path d="M50 86 Q58 86 60 80" />
    <path d="M50 91 Q44 91 42 88" opacity="0.7" />
  </svg>
);

const REGISTRY = {
  visionary: VisionaryIllustration,
  warrior: WarriorIllustration,
  scholar: ScholarIllustration,
  feeler: FeelerIllustration,
  oracle: OracleIllustration,
  nurturer: NurturerIllustration,
};

export const ArchetypeIllustration = ({ archetypeKey, size = 120, ...rest }) => {
  const Cmp = REGISTRY[archetypeKey] || VisionaryIllustration;
  return <Cmp size={size} {...rest} />;
};

export default ArchetypeIllustration;
